/* eslint-disable no-shadow */
import * as React from 'react';
import { Control, useController } from 'react-hook-form';
import { CmdError } from '@commander-services/gui-components';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';

export interface IMultipleSelectOption {
  item: string;
  value: string | number;
}

interface IMultipleSelectOptionsInColumns {
  [row: number]: IMultipleSelectOption[];
}

interface IMultiSelectHookFormProps {
  id: string;
  name: string;
  label: string;
  options: IMultipleSelectOption[];
  selectButton: string;
  resetButton: string;
  selectAllButton: string;
  control: Control<any>;
  placeholder?: string;
  required?: boolean;
  onChangeCallback?: (values: number[] | string[]) => void;
  valuesInColumns?: boolean;
  resetButtonTooltip?: string;
  selectButtonTooltip?: string;
}

export default function MultiSelectHookForm(props: IMultiSelectHookFormProps) {
  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = React.useState<boolean>(false);
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const [isCheckedAll, setIsCheckedAll] = React.useState<boolean>(false);

  const {
    field: { ref, onChange, onBlur, value, name },
    fieldState: { invalid, error },
  } = useController({
    name: props.name,
    control: props.control,
    defaultValue: [], // or the default value
  });

  const clickOutsideRef: React.RefObject<any> = React.useRef();

  useOutsideClick(clickOutsideRef, () => {
    setIsDropdownMenuOpen(false);
  });

  useKeyPress(ESCAPE_KEY, () => {
    setIsDropdownMenuOpen(false);
  });

  const handleShowDropdownMenu = (
    event:
      | React.MouseEvent<HTMLLabelElement, MouseEvent>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsDropdownMenuOpen(!isDropdownMenuOpen);
  };

  React.useEffect(() => {
    if (value && value.length > 0) {
      setSelectedItems(value);
    }
    if (value.length === props.options.length && props.options.length !== 0) {
      setIsCheckedAll(true);
    }
  }, [value, props.options.length]);

  React.useEffect(() => {
    if (value && value.length > 0) {
      setSelectedItems(value);
    }
    if (value.length === props.options.length && props.options.length !== 0) {
      setIsCheckedAll(true);
    }
  }, [value, props.options.length]);

  const handleReset = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setSelectedItems([]);
    setIsCheckedAll(false);
    // props.form.setFieldValue(props.field.name, []);
    onChange([]);
    setIsDropdownMenuOpen(false);
  };

  const handleApply = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    // props.form.setFieldValue(props.field.name, selectedItems);
    onChange(selectedItems);
    setIsDropdownMenuOpen(false);
    if (props.onChangeCallback) {
      props.onChangeCallback(selectedItems);
    }
  };

  const handleSelectItem = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    if (selectedItems.includes(selectedValue)) {
      const newSelectedItems = selectedItems.filter((item: string) => item !== selectedValue);
      setSelectedItems(newSelectedItems);
    } else {
      const newSelectedValues = [...selectedItems, selectedValue];
      setSelectedItems(newSelectedValues);
    }
  };

  const getSelectedFieldValues = (fieldValue, selectOptions: IMultipleSelectOption[]): string => {
    const selectedValues = selectOptions
      .filter((option: IMultipleSelectOption) => fieldValue.includes(String(option.value)))
      .map((option: IMultipleSelectOption) => option.item);

    if (selectedValues.length === 0) {
      return '';
    }

    return selectedValues.join(', ');
  };

  const handleSelectAll = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const selectedValues = props.options.map((option: IMultipleSelectOption) => {
      return String(option.value);
    });

    const checkboxElement: HTMLFormElement | null = document.getElementById(
      event.currentTarget.id
    ) as HTMLFormElement;

    if (checkboxElement && checkboxElement.checked) {
      setSelectedItems(selectedValues);
      setIsCheckedAll(true);
    } else {
      setSelectedItems([]);
      setIsCheckedAll(false);
    }
  };

  const selectedFieldValueItems = getSelectedFieldValues(value, props.options);

  const dropdownInRows = () => (
    <ul className="b-dropdown__list" style={{ maxHeight: '200px', overflow: 'auto' }}>
      {props.options.map((option: IMultipleSelectOption, index: number) => (
        <li
          id={`list-${props.id}`}
          key={`${option.item}-${option.value}-${index}`}
          style={props.valuesInColumns ? { width: '25%', float: 'left' } : {}}
        >
          <div className="e-checkbox">
            <label htmlFor={`${props.id}-${index}`} data-value={option.value}>
              <input
                id={`${props.id}-${index}`}
                value={option.value}
                type="checkbox"
                data-value={option.value}
                data-cy={`multiselect-${option.value}`}
                checked={selectedItems.includes(String(option.value))}
                onChange={handleSelectItem}
              />
              <span id={`${props.id}_selectCheckbox`}>{option.item}</span>
            </label>
          </div>
        </li>
      ))}
    </ul>
  );

  const dropdownInColumns = () => {
    const columnData: IMultipleSelectOptionsInColumns = {};
    let row = 0;

    for (let i = 0; i < props.options.length; i += 1) {
      if (!columnData[row]) {
        columnData[row] = [];
      }

      if (i % 4 === 0 && i !== 0) {
        row += 1;
        if (!columnData[row]) {
          columnData[row] = [];
        }
      }
      columnData[row].push(props.options[i]);
    }

    if (Object.keys(columnData).length === 0) {
      return null;
    }

    return (
      <ul className="b-dropdown__list" style={{ maxHeight: '200px', overflow: 'auto' }}>
        {Object.keys(columnData).map((row: string, index: number) => (
          <li key={`row-${row}-${index}`}>
            <div className="row">
              {columnData[row].map((option: IMultipleSelectOption, index: number) => (
                <div className="col-3" key={`row-${row}-col-${index}`}>
                  <div className="e-checkbox">
                    <label htmlFor={`${props.id}-${option.item}`} data-value={option.value}>
                      <input
                        id={`${props.id}-${option.item}`}
                        type="checkbox"
                        data-value={option.value}
                        value={option.value}
                        checked={selectedItems.includes(String(option.value))}
                        onChange={handleSelectItem}
                      />
                      <span id={`${props.id}_selectCheckbox`}>{option.item}</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div
      id={props.id}
      className={`checkbox-group-component form-group ${error && 'form-group-error'}`}
      ref={clickOutsideRef}
    >
      <div>
        <label className="d-block form-label" onClick={handleShowDropdownMenu}>
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
        <div className="d-block">
          <div className="b-dropdown">
            <div id={props.id} className="form-control-labels" onClick={handleShowDropdownMenu}>
              {selectedFieldValueItems || props.placeholder}
            </div>
            {isDropdownMenuOpen && (
              <div className="b-dropdown__content">
                <div id={`header-${props.id}`} className="b-dropdown__header">
                  <div className="e-checkbox">
                    <label htmlFor={`${props.name}-select-all`}>
                      <input
                        id={`${props.name}-select-all`}
                        type="checkbox"
                        onClick={handleSelectAll}
                        checked={isCheckedAll}
                      />
                      <span id={`choose-all-${props.id}`}>{props.selectAllButton}</span>
                    </label>
                  </div>
                </div>
                {props.valuesInColumns ? dropdownInColumns() : dropdownInRows()}
                <div id={`footer-${props.id}`} className="b-dropdown__footer">
                  {/* <a
                    href="#"
                    className="e-button"
                    onClick={handleReset}
                    title={props.resetButtonTooltip || ''}
                  >
                    {props.resetButton}
                  </a> */}
                  <a
                    href="#"
                    title={props.selectButtonTooltip || ''}
                    className="e-button e-button--gray"
                    onClick={handleApply}
                  >
                    {props.selectButton}
                  </a>
                </div>
              </div>
            )}
          </div>
          {error && error.message && <CmdError message={error.message} />}
        </div>
      </div>
    </div>
  );
}
